/**
 * Storno-Vorschlag (F3/F4, `docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md`, § "Entscheidungen"
 * F3/F4 + § "Datenmodell-Skizze für F3"): reine Berechnung aus der strukturierten Stornostaffel
 * (`tenant_settings.cancellation_tiers`, Migration 404, `packages/db/sql/404_cancellation_tiers.sql`)
 * — Tage bis zum Event -> anzuwendender Einbehalt-Prozentsatz -> Einbehalt-Betrag. Bewusst OHNE
 * jeden API-/DB-Zugriff hier, nur Arithmetik, damit sie unit-testbar ist ohne UI/DB
 * (`pages/event-detail.tsx`/`pages/abrechnung.tsx` rufen sie nur auf und rendern das Ergebnis).
 */

/** Ein Eintrag der Stornostaffel — Format siehe Migrationskopf 404: jsonb-Array, ABSTEIGEND nach
 *  `days` sortiert gespeichert (diese Datei sortiert defensiv selbst nochmal, s. u.). */
export interface CancellationTier {
  days: number;
  keepPercent: number;
}

/**
 * Tage zwischen "jetzt" (Default `new Date()`, für Tests überschreibbar) und dem Referenzdatum
 * einer Veranstaltung. Referenz = `dateEnd ?? eventDate` (Konzept-Doc: bei Mehrtages-Events zählt
 * das Ende). Rechnet auf Kalendertagen (Mitternacht beider Zeitpunkte in lokaler Zeit) — dieselbe
 * Methode wie das bestehende `ui/format.ts#daysUntil`, damit "X Tage bis zur Veranstaltung" überall
 * im Produkt dieselbe Zahl zeigt, unabhängig von der Uhrzeit des Aufrufs.
 *
 * Ohne jedes Referenzdatum (beide `null`) -> `null`. Das Ergebnis kann NEGATIV sein: das
 * Referenzdatum liegt bereits in der Vergangenheit (No-Show/verspätete Absage nach dem Termin) —
 * das ist eine gültige Eingabe für `ermittleKeepPercent`, kein Fehlerfall.
 */
export function tageBisEvent(eventDate: string | null, dateEnd: string | null, jetzt: Date = new Date()): number | null {
  const referenz = dateEnd ?? eventDate;
  if (!referenz) return null;
  const ziel = new Date(referenz);
  if (Number.isNaN(ziel.getTime())) return null;
  const z0 = new Date(ziel.getFullYear(), ziel.getMonth(), ziel.getDate()).getTime();
  const h0 = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate()).getTime();
  return Math.round((z0 - h0) / 86_400_000);
}

/**
 * Ermittelt den anzuwendenden Einbehalt-Prozentsatz aus der Stornostaffel für `tage` (Tage bis
 * zum Event, s. `tageBisEvent`, kann negativ sein). Die Tiers werden ABSTEIGEND nach `days`
 * durchgegangen (unabhängig von der Eingabereihenfolge — defensiv selbst sortiert, statt sich auf
 * die gespeicherte Reihenfolge zu verlassen), die ERSTE Regel mit `tier.days <= tage` gewinnt.
 *
 * Ist `tage` kleiner als JEDE vorhandene Schwelle (auch negativ — Termin bereits vergangen/
 * No-Show), gilt automatisch die Regel mit der kleinsten `days`-Zahl (deckt "<48h/No-Show 100%"
 * aus dem Freitext-Beispiel im Migrationskopf ab, exakt wie im Konzept-Dokument beschrieben).
 *
 * Leeres/fehlendes Tiers-Array -> `null` (kein Vorschlag möglich — die UI fällt dann auf den
 * Freitext `cancellationTerms` zurück, ohne Betrag).
 */
export function ermittleKeepPercent(tiers: CancellationTier[] | null | undefined, tage: number): number | null {
  if (!tiers || tiers.length === 0) return null;
  const absteigend = [...tiers].sort((a, b) => b.days - a.days);
  for (const tier of absteigend) {
    if (tage >= tier.days) return tier.keepPercent;
  }
  // `tage` liegt unter jeder vorhandenen Schwelle -> die kleinste `days`-Zahl gewinnt (letzter
  // Eintrag nach der absteigenden Sortierung).
  return absteigend[absteigend.length - 1]!.keepPercent;
}

/** `bezahlterBetragCents * keepPercent / 100`, kaufmännisch gerundet auf ganze Cent. */
export function berechneEinbehalt(bezahlterBetragCents: number, keepPercent: number): number {
  return Math.round((bezahlterBetragCents * keepPercent) / 100);
}
